import React, { useState, useEffect } from "react";
// prettier-ignore
import { Grid, FormControlLabel, TextField, Typography, Snackbar, InputAdornment, Tooltip } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InfoIcon from "@mui/icons-material/Info";
import throttle from "lodash/throttle";
import parse from "autosuggest-highlight/parse";
import { getJsonApi } from "../../modules/AmplifyServices";
import { Autocomplete } from "@mui/material";
import { useStyles } from "../../styles/MainContainerStyles";
import { padding } from "@mui/system";
import InfoTooltip from "../../utils/UI/InfoTooltip";
import {
    normalizeUnit,
    combineUnitWithAddress,
    setUnitOnAddress,
    splitUnitFromAddress,
    extractTypedUnit
} from "../../utils/addressUnit";

function loadScript(src, position, id) {
    if (!position) {
        return;
    }

    const script = document.createElement("script");
    script.setAttribute("async", "");
    script.setAttribute("id", id);
    script.src = src;
    position.appendChild(script);
}

export const AddressEntryTextBox = (props) => {
    const classes = useStyles();
    const [optionsGM, setOptionsGM] = React.useState([]);
    const initialSplit = splitUnitFromAddress(props.addressOneLine || "");
    const [inputValue, setInputValue] = React.useState(
        initialSplit.baseAddress
    );
    const [unit, setUnit] = useState(initialSplit.unit);
    const [place_id, setPlaceId] = useState();
    // last combined addressOneLine emitted to the parent — distinguishes a
    // parent echo from a genuinely external address (edit flows)
    const lastEmittedAddressRef = React.useRef("");
    // raw text the user typed, captured before Google's canonical address
    // overwrites the input
    const lastTypedInputRef = React.useRef("");
    const lastPlaceDetailsRef = React.useRef(null);
    const unitDirtyRef = React.useRef(false);

    const [msgOpen, setMsgOpen] = useState(false);
    const [snackMsg, setSnackMsg] = useState("");
    const [isOpen, setIsOpen] = useState(false);

    const loaded = React.useRef(false);
    useEffect(() => {
        if (typeof window !== "undefined" && !loaded.current) {
            if (!document.querySelector("#google-maps")) {
                const url =
                    "https://maps.googleapis.com/maps/api/js?key=" +
                    process.env.REACT_APP_GOOGLE_MAP_KEY +
                    "&libraries=places&v=3.64";
                loadScript(url, document.querySelector("head"), "google-maps");
            }

            loaded.current = true;
        }
    }, []);

    let autocompleteService = props.autocompleteService;
    async function getPlaceDetails() {
        const result = await getJsonApi(
            "mapdirection",
            `/placedetails/${place_id}`
        );
        if (result && result.success) {
            return result.placedetails;
        } else {
            return null;
        }
    }
    const fetch = React.useMemo(
        () =>
            throttle((input, callback) => {
                autocompleteService.current.getPlacePredictions(
                    input,
                    callback
                );
            }, 200),
        []
    );

    useEffect(() => {
        let active = true;

        if (!autocompleteService.current && window.google) {
            autocompleteService.current =
                new window.google.maps.places.AutocompleteService();
        }
        if (!autocompleteService.current) {
            return undefined;
        }

        if (inputValue === "") {
            setOptionsGM([]);
            return ""; //undefined;
        }
        if (inputReady(inputValue))
            fetch({ input: inputValue }, (results) => {
                if (active) {
                    setOptionsGM(results || []);
                }
            });

        return () => {
            active = false;
        };
    }, [inputValue, fetch]);

    function inputReady(inp) {
        if (inp && inp.length > 3) {
            inp = inp.trimStart();
            if (inp.includes(" ")) {
                let spaceInd = inp.indexOf(" ");
                if (inp.length - spaceInd >= 5) return true;
            }
        }

        return false;
    }

    useEffect(() => {
        if (!props.addressOneLine) {
            setInputValue("");
            setUnit("");
            lastEmittedAddressRef.current = "";
            lastTypedInputRef.current = "";
            lastPlaceDetailsRef.current = null;
            unitDirtyRef.current = false;
            return;
        }
        // ignore the parent echoing back the combined address we just sent
        if (props.addressOneLine === lastEmittedAddressRef.current) return;
        // external address (edit flow): show base address, prefill the unit
        const { unit: savedUnit, baseAddress } = splitUnitFromAddress(
            props.addressOneLine
        );
        setUnit(savedUnit);
        setInputValue(baseAddress);
    }, [props.addressOneLine]);

    useEffect(() => {
        async function getPlaceAddress() {
            try {
                const placeDetails = await getPlaceDetails();
                if (placeDetails) {
                    if (
                        placeDetails.city ||
                        placeDetails.route ||
                        placeDetails.street_number ||
                        placeDetails.postal_code
                    ) {
                        // keep a typed or Google-provided unit; input shows
                        // the base address, the unit lives in its own field
                        // and is combined into the emitted addressOneLine
                        const detectedUnit =
                            unit ||
                            extractTypedUnit(
                                lastTypedInputRef.current,
                                placeDetails.addressOneLine
                            ) ||
                            normalizeUnit(placeDetails.subpremise || "") ||
                            // last resort: Google's own address often spells
                            // the unit out ("#714", "flat 3", "5/12")
                            splitUnitFromAddress(placeDetails.addressOneLine)
                                .unit;
                        if (detectedUnit !== unit) setUnit(detectedUnit);
                        const combined = combineUnitWithAddress(
                            detectedUnit,
                            placeDetails.addressOneLine
                        );
                        lastPlaceDetailsRef.current = placeDetails;
                        lastEmittedAddressRef.current = combined;
                        // the box shows the address without the unit; the
                        // unit is shown in its own field beside it
                        setInputValue(
                            splitUnitFromAddress(placeDetails.addressOneLine)
                                .baseAddress
                        );
                        props.setAddress(
                            combined,
                            placeDetails.route,
                            placeDetails.city,
                            placeDetails.state,
                            placeDetails.postal_code,
                            placeDetails.country,
                            placeDetails.location.lat,
                            placeDetails.location.lng,
                            true
                        );
                    } else {
                        setSnackMsg(
                            "Please provide a street address for your location."
                        );
                        setMsgOpen(true);
                    }
                } else {
                    console.log("Could not get place details.");
                }
            } catch (e) {
                console.log("error getting place: ", e);
            }
        }
        if (place_id) getPlaceAddress();
    }, [place_id]);

    const handleAddressChange = (event, values) => {
        try {
            setPlaceId(values.place_id);
            setIsOpen(false);
        } catch (err) {
            console.log(
                "An error occurred in handleAddressChange. Error was " +
                    JSON.stringify(err)
            );
        }
    };
    const handleChange = (event) => {
        setIsOpen(true);
        lastTypedInputRef.current = event.target.value;
        setInputValue(event.target.value);
    };

    // re-emit the combined address when the unit is edited after an address
    // has already been selected (or cleared — reverts to the base address)
    const handleUnitChange = (event) => {
        const newUnit = event.target.value;
        setUnit(newUnit);
        unitDirtyRef.current = true;
        const pd = lastPlaceDetailsRef.current;
        if (pd) {
            // setUnitOnAddress, not combine: the user is overriding whatever
            // unit the address already carried, so the old one must go
            const combined = setUnitOnAddress(newUnit, pd.addressOneLine);
            lastEmittedAddressRef.current = combined;
            props.setAddress(
                combined,
                pd.route,
                pd.city,
                pd.state,
                pd.postal_code,
                pd.country,
                pd.location.lat,
                pd.location.lng,
                true
            );
            unitDirtyRef.current = false;
        }
    };

    // fallback: unit edited on a saved address that never went through
    // place-details in this session — resolve the base address to a place
    // so the full positional payload can be re-emitted with the unit
    const handleUnitBlur = () => {
        if (!unitDirtyRef.current) return;
        if (lastPlaceDetailsRef.current) return;
        if (!inputValue || !autocompleteService.current) return;
        fetch({ input: inputValue }, (results) => {
            setOptionsGM(results || []);
            if (results?.[0]?.place_id) setPlaceId(results[0].place_id);
        });
        unitDirtyRef.current = false;
    };

    return (
        <>
            <Snackbar
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center"
                }}
                open={msgOpen}
                autoHideDuration={5000}
                onClose={() => setMsgOpen(false)}
                ContentProps={{
                    "aria-describedby": "message-id"
                }}
                message={<span id="message-id">{snackMsg}</span>}
            />
            {/* the unit field must stay OUTSIDE the FormControlLabel — a
                <label> redirects clicks to its first input (the address
                box), which would make the unit field unfocusable */}
            <div
                style={{
                    display: "flex",
                    gap: "8px",
                    // stretch so the unit field always matches the address
                    // box height, whatever the variant/adornments compute to
                    alignItems: "stretch",
                    ...(props.style
                        ? props.style
                        : {
                              width: "100%",
                              marginLeft: "0px",
                              marginTop: props.marginAbove ? "1rem" : "32px",
                              marginBottom: "16px"
                          })
                }}
            >
            <FormControlLabel
                sx={{ position: "relative" }}
                labelPlacement="top"
                style={{
                    flex: 1,
                    minWidth: 0,
                    margin: 0,
                    display: "inline-block"
                }}
                control={
                    <Autocomplete
                        disablePortal={true}
                        id="user-address"
                        inputValue={inputValue}
                        getOptionLabel={(optionGM) => {
                            return optionGM.description || "";
                        }}
                        filterOptions={(x) => x}
                        options={optionsGM}
                        classes={{
                            inputRoot: classes.inputRoot1
                        }}
                        autoComplete
                        open={isOpen}
                        onChange={handleAddressChange}
                        includeInputInList
                        freeSolo
                        openOnFocus
                        style={{
                            width: "100%",
                            outline: "5px solid red",
                            position: "relative"
                        }}
                        renderInput={(params) => (
                            <TextField
                                size={props.fromSettingsModal && "small"}
                                sx={{
                                    backgroundColor: "white",
                                    border:
                                        !props.fromSettingsModal &&
                                        "2px solid #d4d4d4",
                                    borderRadius:
                                        !props.fromSettingsModal && "4px",
                                    "&:hover": { backgroundColor: "white" },
                                    "& .MuiFilledInput-root": {
                                        "&:hover": { backgroundColor: "white" },
                                        backgroundColor: "white",
                                        "&.Mui-focused": {
                                            backgroundColor: "white"
                                        }
                                    }
                                }}
                                variant={
                                    props.fromSettingsModal
                                        ? "outlined"
                                        : "filled"
                                }
                                {...params}
                                InputProps={{
                                    ...params.InputProps,
                                    style: props.fromSettingsModal && {
                                        padding: 0,
                                        paddingLeft: "8px"
                                    },
                                    disableUnderline: true,
                                    endAdornment:
                                        props.fromDashboardInStore ||
                                        props.fromDashboardOnline ? (
                                            <InputAdornment position="end">
                                                <Tooltip
                                                    title={
                                                        "This address will be used to determine which time zone the suggested time slots are shown to clients"
                                                    }
                                                    placement="bottom"
                                                    style={{
                                                        color: "grey",
                                                        fontSize: "20px",
                                                        marginBottom: "20px"
                                                    }}
                                                >
                                                    <InfoIcon />
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : props?.tooltipTitle ? (
                                            <InputAdornment
                                                position="end"
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "flex-end",
                                                    ...props.sx
                                                }}
                                            >
                                                <Tooltip
                                                    title={props.tooltipTitle}
                                                    placement="bottom"
                                                    style={{
                                                        color: "#a2a2a2",
                                                        fontSize: "20px"
                                                    }}
                                                >
                                                    {props.infoTooltip ? (
                                                        <InfoTooltip
                                                            title={
                                                                props.tooltipTitle
                                                            }
                                                        />
                                                    ) : (
                                                        <InfoIcon />
                                                    )}
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : (
                                            ""
                                        )
                                }}
                                InputLabelProps={{
                                    ...params.InputLabelProps,
                                    sx: {
                                        top: "-6px"
                                    }
                                }}
                                label={props.label ? props.label : "Address"}
                                fullWidth
                                onChange={handleChange}
                            />
                        )}
                        renderOption={(props, option) => {
                            const matches =
                                option.structured_formatting
                                    .main_text_matched_substrings || [];

                            const parts = parse(
                                option.structured_formatting.main_text,
                                matches.map((match) => [
                                    match.offset,
                                    match.offset + match.length
                                ])
                            );

                            return (
                                <li sx={{ position: "relative" }} {...props}>
                                    <Grid
                                        sx={{ position: "relative" }}
                                        container
                                        alignItems="center"
                                    >
                                        <Grid item>
                                            <LocationOnIcon
                                                className={classes.icon}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            {parts.map((part, index) => (
                                                <span
                                                    key={index}
                                                    style={{
                                                        fontWeight:
                                                            part.highlight
                                                                ? 700
                                                                : 400
                                                    }}
                                                >
                                                    {part.text}
                                                </span>
                                            ))}

                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {
                                                    option.structured_formatting
                                                        .secondary_text
                                                }
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </li>
                            );
                        }}
                    />
                }
            />
                    {!props.hideUnitField && (
                        <TextField
                            id="user-address-unit"
                            label={props.unitLabel || "Unit/Apt #"}
                            value={unit}
                            onChange={handleUnitChange}
                            onBlur={handleUnitBlur}
                            size={props.fromSettingsModal ? "small" : "medium"}
                            variant={
                                props.fromSettingsModal ? "outlined" : "filled"
                            }
                            sx={{
                                width: "100px",
                                flexShrink: 0,
                                backgroundColor: "white",
                                border:
                                    !props.fromSettingsModal &&
                                    "2px solid #d4d4d4",
                                borderRadius: !props.fromSettingsModal && "4px",
                                // fill the stretched flex row so the box is
                                // exactly as tall as the address input
                                "& .MuiInputBase-root": {
                                    height: "100%"
                                },
                                "& .MuiFilledInput-root": {
                                    backgroundColor: "white",
                                    "&:hover": { backgroundColor: "white" },
                                    "&.Mui-focused": {
                                        backgroundColor: "white"
                                    }
                                }
                            }}
                            /* mirror the address input's paddings so both
                               boxes get the same height in the compact
                               settings-modal variant */
                            inputProps={{
                                maxLength: 16,
                                style: props.fromSettingsModal
                                    ? { padding: "2.5px 8px" }
                                    : {}
                            }}
                            InputProps={{
                                disableUnderline: true,
                                style: props.fromSettingsModal
                                    ? { padding: 0 }
                                    : {}
                            }}
                            InputLabelProps={{
                                sx: {
                                    top: "-6px",
                                    // MUI's resting-label offset assumes the
                                    // default input height; center it in the
                                    // stretched box instead
                                    "&:not(.MuiInputLabel-shrink)": {
                                        top: "50%",
                                        transform: props.fromSettingsModal
                                            ? "translate(14px, -50%)"
                                            : "translate(12px, -50%)"
                                    }
                                }
                            }}
                        />
                    )}
            </div>
        </>
    );
};
